import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  FileArrowDown,
  Calendar,
  ChartLineUp,
  Sparkle,
  Warning,
} from '@phosphor-icons/react';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import {
  weeklyReportsEndpoints,
  IWeeklyReport,
} from '../../endpoints/weeklyReports';
import { WeeklyReportView } from './WeeklyReportView';
import styles from './WeeklyReports.module.scss';

dayjs.extend(isoWeek);

/**
 * Archivio Weekly Reports del team.
 * Spec v1.1.1 sez 14 (righe 1131-1267).
 * Lista reports + viewer web interattivo singolo + export PDF.
 */
const WeeklyReportsPage: FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['weeklyReports', teamId],
    queryFn: () =>
      weeklyReportsEndpoints.list(teamId ?? '', 50).then((r) => r.data),
    enabled: !!teamId,
  });

  const generateMut = useMutation({
    mutationFn: () =>
      weeklyReportsEndpoints
        .generate(teamId ?? '', dayjs().startOf('isoWeek').format('YYYY-MM-DD'))
        .then((r) => r.data),
    onSuccess: (rep: IWeeklyReport) => {
      queryClient.invalidateQueries({ queryKey: ['weeklyReports', teamId] });
      setSelectedId(rep._id);
    },
  });

  const handleExportPdf = async (id: string) => {
    const res = await weeklyReportsEndpoints.exportPdf(id);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weekly-report-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          <div className={styles.toolbar}>
            <Typhography type="h1" size="bold-20">
              Report Settimanali
            </Typhography>
            <CustomButton
              color="secondary"
              size="sm"
              onClick={() => generateMut.mutate()}
              isLoading={generateMut.isPending}
            >
              Genera ora
            </CustomButton>
          </div>

          {isLoading && (
            <div className={styles.loading}>
              <LeopartLoader size="md" />
            </div>
          )}

          {!isLoading && reports.length === 0 && (
            <div className={styles.emptyState}>
              <Sparkle size={32} color="#b5b5b5" weight="fill" />
              <Typhography size="bold-14">Nessun report</Typhography>
              <span className={styles.emptyHint}>
                I report vengono generati automaticamente la domenica sera. Usa
                "Genera ora" per uno manuale.
              </span>
            </div>
          )}

          {!isLoading && reports.length > 0 && (
            <div className={styles.layout}>
              <div className={styles.list}>
                {reports.map((r) => (
                  <ReportRow
                    key={r._id}
                    report={r}
                    active={selectedId === r._id}
                    onClick={() => setSelectedId(r._id)}
                    onExport={() => handleExportPdf(r._id)}
                  />
                ))}
              </div>
              <div className={styles.viewer}>
                {selectedId ? (
                  <WeeklyReportView
                    reportId={selectedId}
                    onExport={handleExportPdf}
                  />
                ) : (
                  <div className={styles.viewerEmpty}>
                    <ChartLineUp size={24} color="#b5b5b5" />
                    <span>Seleziona un report dalla lista</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportRow: FC<{
  report: IWeeklyReport;
  active: boolean;
  onClick: () => void;
  onExport: () => void;
}> = ({ report, active, onClick, onExport }) => {
  const { summary, atRiskAthletes } = report;
  const hasRisk = (atRiskAthletes?.length ?? 0) > 0;

  return (
    <div
      className={`${styles.row} ${active ? styles.rowActive : ''}`}
      onClick={onClick}
    >
      <div className={styles.rowHeader}>
        <div className={styles.rowTitle}>
          <Calendar size={14} />
          Settimana {report.weekNumber} · {report.season}
        </div>
        <button
          type="button"
          className={styles.exportBtn}
          onClick={(e) => {
            e.stopPropagation();
            onExport();
          }}
          title="Esporta PDF"
        >
          <FileArrowDown size={14} />
        </button>
      </div>
      <div className={styles.rowMeta}>
        {dayjs(report.startDate).format('D MMM')} –{' '}
        {dayjs(report.endDate).format('D MMM YYYY')}
      </div>
      <div className={styles.rowStats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Sessioni</span>
          <span className={styles.statValue}>
            {summary.sessionsExecuted}/{summary.sessionsPlanned}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>CE</span>
          <span className={styles.statValue}>
            {summary.totalCE?.toFixed(0) ?? '—'}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>ACWR</span>
          <span className={styles.statValue}>
            {summary.acwrEnd?.toFixed(2) ?? '—'}
          </span>
        </div>
      </div>
      {hasRisk && (
        <div className={styles.riskPill}>
          <Warning size={12} weight="fill" />
          {atRiskAthletes!.length} atleta/i a rischio
        </div>
      )}
    </div>
  );
};

export default WeeklyReportsPage;
